/**
 * Helpers for the billing cycle (Abrechnungszyklus) of a contract.
 *
 * Operators choose the billing cycle for contracts with billing (WITH_BILLING).
 * It can be changed while the contract is not active yet (CREATED_UNSIGNED,
 * CREATED_SIGNED); later changes are done by the support.
 */

export const DEFAULT_BILLING_CYCLE = 'MONTHLY';

// the billing cycles an operator can choose, values as used by the contract management API
export const SELECTABLE_BILLING_CYCLES = [
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
];

export const BILLING_CYCLE_EDITABLE_CONTRACT_STATUSES = [
  'CREATED_UNSIGNED',
  'CREATED_SIGNED',
];

/**
 * @param {string} billingType - 'WITH_BILLING' or 'NO_BILLING'
 * @returns {boolean} - the billing cycle only applies to contracts with billing
 */
export const isBillingCycleVisible = (billingType) =>
  billingType === 'WITH_BILLING';

/**
 * @param {boolean} isEdit - true in the edit workflow, false on user creation
 * @param {string} contractStatus - status of the edited contract
 * @returns {boolean} - true on creation and for contracts that are not active yet
 */
export const isBillingCycleEditable = (isEdit, contractStatus) =>
  !isEdit || BILLING_CYCLE_EDITABLE_CONTRACT_STATUSES.includes(contractStatus);

/**
 * @param {string} billingCycle - billing cycle of the contract, if any
 * @returns {string} - the billing cycle the form starts with (MONTHLY for new contracts)
 */
export const getInitialBillingCycle = (billingCycle) =>
  billingCycle || DEFAULT_BILLING_CYCLE;

/**
 * @param {string} currentBillingCycle - billing cycle shown in the form
 * @returns {string[]} - the selectable billing cycles, plus the current one if the support set another
 * one (e.g. ANNUAL), so a disabled field still shows it
 */
export const getBillingCycleOptions = (currentBillingCycle) =>
  !currentBillingCycle ||
  SELECTABLE_BILLING_CYCLES.includes(currentBillingCycle)
    ? SELECTABLE_BILLING_CYCLES
    : [...SELECTABLE_BILLING_CYCLES, currentBillingCycle];

/**
 * The billing cycle is only sent when it is shown and may be changed, otherwise
 * the contract management keeps the stored one.
 * @param {{ isEdit: boolean, contractStatus: string, billingType: string, billingCycle: string }} params
 * @returns {string | undefined} - the billing cycle to send or undefined
 */
export const getBillingCycleToSend = ({
  isEdit,
  contractStatus,
  billingType,
  billingCycle,
}) =>
  isBillingCycleVisible(billingType) &&
  isBillingCycleEditable(isEdit, contractStatus)
    ? billingCycle || DEFAULT_BILLING_CYCLE
    : undefined;
